// TelcoTrack — trasu s oběma ceníky výkopu (metry i m³) lze otevřít a číst.
// Převod do další fáze zůstává zakázaný (`routeSegmentsMixExcavationPricing`).

import { type ExcavationPricingMode } from '../constants/excavation-material.constants';
import {
  inferExcavationPricingModeFromSurfaces,
  type ExcavationPricingModeEvidence,
} from './excavation-pricing-mode.util';

export const ROUTE_MIXED_EXCAVATION_PRICING_MESSAGE =
  'Trasu nelze převést do další fáze, dokud se míchají ceníky.';

export type RouteEdgePricing = ExcavationPricingMode | 'conflict';

export function excavationPricingBoundaryMessage(established: ExcavationPricingMode): string {
  return established === 'linear'
    ? 'Tady končí ceník v metrech. Vedlejší úsek je v m³ — označte ho zvlášť.'
    : 'Tady končí ceník v m³. Vedlejší úsek je v metrech — označte ho zvlášť.';
}

export function routeEdgeKey(ha: string, hb: string): string {
  return ha <= hb ? `${ha}\u001f${hb}` : `${hb}\u001f${ha}`;
}

/** Explicitní režim z metadat segmentu. Bez důkazu (ještě bez povrchů) vrací undefined. */
export function readRouteSegmentPricingMode(metadata: unknown): ExcavationPricingMode | undefined {
  if (!metadata || typeof metadata !== 'object') return undefined;
  return inferExcavationPricingModeFromSurfaces(metadata as ExcavationPricingModeEvidence);
}

export function noteRouteEdgePricing(
  map: Map<string, RouteEdgePricing>,
  edgeKey: string,
  mode: ExcavationPricingMode | undefined,
): void {
  if (!mode) return;
  const prev = map.get(edgeKey);
  if (!prev) {
    map.set(edgeKey, mode);
    return;
  }
  if (prev !== mode) map.set(edgeKey, 'conflict');
}

export function establishedRoutePricingMode(
  edgeModes: ReadonlyMap<string, RouteEdgePricing>,
  path: readonly string[],
): ExcavationPricingMode | undefined {
  for (let i = 0; i < path.length - 1; i++) {
    const mode = edgeModes.get(routeEdgeKey(path[i], path[i + 1]));
    if (mode === 'linear' || mode === 'volume') return mode;
  }
  return undefined;
}

/**
 * Otevření trasy smí přejít přes oba ceníky. Zámek přechodu fáze je
 * `routeSegmentsMixExcavationPricing`, ne výběr vrcholů.
 */
export function routeEdgeAllowed(
  _lock: ExcavationPricingMode | undefined,
  _edge: RouteEdgePricing | undefined,
): boolean {
  return true;
}

/** Kopie sousedství bez hran, které by z aktuální trasy přešly do druhého ceníku. */
export function adjacencyGuardedByExcavationPricing(
  adjacency: ReadonlyMap<string, readonly string[]>,
  edgeModes: ReadonlyMap<string, RouteEdgePricing>,
  path: readonly string[],
): Map<string, string[]> {
  const lock = establishedRoutePricingMode(edgeModes, path);
  const out = new Map<string, string[]>();
  for (const [hash, neighbors] of adjacency) {
    out.set(
      hash,
      neighbors.filter((next) => routeEdgeAllowed(lock, edgeModes.get(routeEdgeKey(hash, next)))),
    );
  }
  return out;
}

/**
 * BFS, který po první explicitní hraně zamkne ceník. Nedojeď na vrchol za hranicí ceníků.
 * `initialLock` je režim už vybrané trasy (prázdný, dokud na ní žádná sazba není).
 */
export function findPathRespectingExcavationPricing(
  adjacency: ReadonlyMap<string, readonly string[]>,
  edgeModes: ReadonlyMap<string, RouteEdgePricing>,
  fromHash: string,
  toHash: string,
  initialLock: ExcavationPricingMode | undefined,
): string[] | null {
  if (fromHash === toHash) return [fromHash];
  const visited = new Set<string>([`${fromHash}|${initialLock ?? ''}`]);
  const queue: Array<{ hash: string; path: string[]; lock: ExcavationPricingMode | undefined }> = [
    { hash: fromHash, path: [fromHash], lock: initialLock },
  ];
  while (queue.length > 0) {
    const current = queue.shift()!;
    for (const next of adjacency.get(current.hash) ?? []) {
      const edge = edgeModes.get(routeEdgeKey(current.hash, next));
      if (!routeEdgeAllowed(current.lock, edge)) continue;
      const nextLock: ExcavationPricingMode | undefined =
        edge === 'linear' || edge === 'volume' ? (current.lock ?? edge) : current.lock;
      const visitKey = `${next}|${nextLock ?? ''}`;
      if (visited.has(visitKey)) continue;
      visited.add(visitKey);
      const nextPath = [...current.path, next];
      if (next === toHash) return nextPath;
      queue.push({ hash: next, path: nextPath, lock: nextLock });
    }
  }
  return null;
}

/** True, když mezi metadaty segmentů jsou zároveň metry i m³. */
export function routeSegmentsMixExcavationPricing(metadatas: readonly unknown[]): boolean {
  let linear = false;
  let volume = false;
  for (const meta of metadatas) {
    const mode = readRouteSegmentPricingMode(meta);
    if (mode === 'linear') linear = true;
    if (mode === 'volume') volume = true;
    if (linear && volume) return true;
  }
  return false;
}
